import { createRouter, createWebHistory } from 'vue-router';
import UserLogin from '../components/UserLogin.vue';
import UserProfile from '../components/UserProfile.vue';
import MemberDirectory from '../components/MemberDirectory.vue';
import EventsCalendar from '../components/EventsCalendar.vue';
import EventManagement from '../components/EventManagement.vue';
import VenueManagement from '../components/VenueManagement.vue';
import UserDashboard from '../components/UserDashboard.vue';
import EmailTemplateManager from '@/components/EmailTemplateManager.vue';
import VisitorManagement from '../components/VisitorManagement.vue';
import PastEvents from '../components/PastEvents.vue';
import TasksView from '../components/TasksView.vue';
import Goals from '../components/Goals.vue';
import AdminUsers from '../components/AdminUsers.vue';
import MeetingFormatManagement from '../components/MeetingFormatManagement.vue';
import SpeakerRoster from '../components/SpeakerRoster.vue';
import TokenCheckinPage from '../components/TokenCheckinPage.vue';
import PasswordReset from '../components/PasswordReset.vue';
import nativeAuthService from '@/services/nativeAuthService';

// Menu options and their corresponding components
// Dashboard - UserDashboard.vue
// Events - EventManagement.vue
// Directory - MemberDirectory.vue
// Visitors - VisitorManagement.vue
// Goals - Goals.vue
// Member Admin - AdminUsers.vue
// Venues - VenueManagement.vue
// Profile - UserProfile.vue
// Email Templates - EmailTemplateManager.vue
// Past Events - PastEvents.vue
// Tasks - TasksView.vue


const routes = [
    { 
        path: '/',
        redirect: () => {
            return nativeAuthService.isAuthenticated() ? '/dashboard' : '/login';
        }
    },
    { 
        path: '/login', 
        name: 'login',
        component: UserLogin 
    },
    {
        path: '/reset-password',
        name: 'password-reset',
        component: PasswordReset
    },
    {
        path: '/checkin/:token',
        name: 'token-checkin',
        component: TokenCheckinPage,
        props: true
    },
    { 
        path: '/dashboard', 
        name: 'Dashboard',
        component: UserDashboard,
        meta: { requiresAuth: true }
    },
    {
        path: '/admin',
        name: 'admin',
        component: AdminUsers,
        meta: { requiresAuth: true, requiresAdmin: true },
        beforeEnter: (to, from, next) => {
            next();
        }
    },
    {
        path: '/profile',
        name: 'Profile',
        component: UserProfile,
        meta: { requiresAuth: true }
    },
    { 
        path: '/directory',
        name: 'Directory',
        component: MemberDirectory,
        meta: { requiresAuth: true }
    },
    {
        path: '/events',
        name: 'Events',
        component: EventsCalendar,
        meta: { requiresAuth: true }
    },
    {
        path: '/admin/events',
        name: 'event-management',
        component: EventManagement,
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin/venues',
        name: 'venue-management',
        component: VenueManagement,
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin/meeting-formats',
        name: 'meeting-format-management',
        component: MeetingFormatManagement,
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin/speaker-roster',
        name: 'speaker-roster',
        component: SpeakerRoster,
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin/users',
        name: 'AdminUsers',
        redirect: '/admin'
    },
    {
        path: '/goals',
        name: 'Goals',
        component: Goals,
        meta: { requiresAuth: true }
    },
    {
        path: '/past-events',
        name: 'PastEvents',
        component: PastEvents,
        meta: { requiresAuth: true }
    },
    {
        path: '/email-templates',
        name: 'EmailTemplates',
        component: EmailTemplateManager,
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/visitors',
        name: 'Visitors',
        component: VisitorManagement,
        meta: { requiresAuth: true }
    },
    {
        path: '/tasks',
        name: 'Tasks',
        component: TasksView,
        meta: { requiresAuth: true }
    },
    {
        path: '/tasks/:id',
        name: 'TaskDetails',
        component: TasksView,
        props: true,
        meta: { requiresAuth: true }
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

router.beforeEach(async (to, from, next) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check native authentication
        let isAuthenticated = nativeAuthService.isAuthenticated();
        if (isAuthenticated) {
            // Validate token to ensure it's still valid
            const tokenValidation = await nativeAuthService.validateToken();
            isAuthenticated = tokenValidation.valid;
        }

        if (!isAuthenticated) {
            next('/login');
            return;
        }

        // Check admin access for admin routes
        if (to.matched.some(record => record.meta.requiresAdmin)) {
            try {
                const isAdmin = nativeAuthService.isAdmin();

                if (!isAdmin) {
                    next('/dashboard');
                    return;
                }
            } catch (error) {
                console.error('Error checking admin status:', error);
                next('/dashboard');
                return;
            }
        }
        next();
    } else {
        next();
    }
});

export default router; 